/* Unit viewer + lesson player (02 §3.3, §3.3.1).
 *
 * The Complete gate is load-bearing, not decoration: L1 "Studied" is the mastery
 * level a lesson grants, and 02 §3.3.1 says the button enables only after the
 * FINAL section has been scrolled into view. An IntersectionObserver on the last
 * section is the honest implementation — a timer or a scroll-percentage guess
 * would let a learner claim mastery they did not read.
 *
 * Completion is queued LOCALLY at M1. The sync route that drains it,
 * POST /api/reviews with the `unit_completion` event type, arrives at M2 —
 * which is also why the old KG-1 note is stale: that endpoint already exists in
 * the contract (05 §3.4), so no 28th route is invented here.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiFailure, type UnitDto } from '../api';
import { Markdown } from '../markdown';
import { isUnitCompleted, queueUnitCompletion } from '../sync';
import { BuildScreen } from './build';

const SOURCE_KIND_LABEL: Record<string, string> = {
  course: 'Course', doc: 'Document', generated: 'Generated',
};

const UNIT_TYPE_LABEL: Record<string, string> = {
  lesson: 'Lesson', drill_deck: 'Drill deck', quiz: 'Quiz', mission: 'Mission',
  lab: 'Lab', build: 'Build',
};

/* Provenance — the "why am I learning this" surface required by N8. Every Unit
 * type carries it, so it lives outside the lesson player. */
function SourcesCard({
  unit, open, onToggle,
}: {
  unit: UnitDto;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="card card--sources">
      <button type="button" className="sources__toggle" onClick={onToggle}>
        Why am I learning this? {open ? '▾' : '▸'}
      </button>
      {open ? (
        <div className="sources">
          <p className="hint">
            Regime {unit.provenance.regime}
            {unit.provenance.regime === 'A'
              ? ' — distilled from academic course material.'
              : ' — practice/frontier content; sources and generation notes below.'}
          </p>
          <ul className="sources__list">
            {unit.provenance.sources.map((s, i) => (
              <li key={`${s.kind}-${s.ref}-${i}`}>
                <span className="sources__kind">{SOURCE_KIND_LABEL[s.kind] ?? s.kind}</span>
                <code>{s.ref}</code>
                {s.note ? <span className="sources__note">{s.note}</span> : null}
              </li>
            ))}
          </ul>
          {unit.provenance.verify_by ? (
            <p className="hint">Time-sensitive facts to reverify by {unit.provenance.verify_by}.</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function UnitScreen({ unitId, onBack }: { unitId: string; onBack: () => void }) {
  const [unit, setUnit] = useState<UnitDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [showSources, setShowSources] = useState(false);
  const lastSectionRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setUnit(await api.unit(unitId));
      } catch (err) {
        setError(
          err instanceof ApiFailure
            ? err.status === 404
              ? 'retired'
              : err.code === 'OFFLINE'
                ? 'offline'
                : err.message
            : 'Something went wrong.',
        );
      }
    })();
  }, [unitId]);

  // Watch the LAST section. Once seen, the gate stays open — scrolling back up
  // must not re-lock a lesson the learner has genuinely reached the end of.
  useEffect(() => {
    const el = lastSectionRef.current;
    if (!el || reachedEnd) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setReachedEnd(true);
          io.disconnect();
        }
      },
      { threshold: 0.6 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [unit, reachedEnd]);

  useEffect(() => {
    void isUnitCompleted(unitId).then(setCompleted);
  }, [unitId]);

  const complete = useCallback(async () => {
    // Await the durable write before showing it as done.
    await queueUnitCompletion(unitId);
    setCompleted(true);
  }, [unitId]);

  if (error === 'retired') {
    return (
      <main className="screen">
        <button type="button" className="back" onClick={onBack}>← Back</button>
        <p className="eyebrow">Archived content</p>
        <h1>This Unit is no longer in the active release</h1>
        <p>
          Your progress on it is kept — learner state is never deleted by a release.
          It has simply stopped being offered as new work.
        </p>
      </main>
    );
  }

  if (error) {
    return (
      <main className="screen">
        <button type="button" className="back" onClick={onBack}>← Back</button>
        <h1>Could not open this Unit</h1>
        <p>{error === 'offline' ? 'Not cached, and you are offline. Connect or sync first.' : error}</p>
      </main>
    );
  }

  if (!unit) {
    return (
      <main className="screen">
        <button type="button" className="back" onClick={onBack}>← Back</button>
        <div className="skeleton skeleton--line" />
        <div className="skeleton skeleton--block" />
      </main>
    );
  }

  // A build unit is not a document to read — it has its own player (CR-020).
  if (unit.type === 'build') {
    return <BuildScreen unit={unit} onBack={onBack} />;
  }

  if (unit.type !== 'lesson') {
    const cards = unit.cards?.length ?? 0;
    return (
      <main className="screen">
        <button type="button" className="back" onClick={onBack}>← Back</button>
        <p className="eyebrow">{UNIT_TYPE_LABEL[unit.type] ?? unit.type} · {unit.est_minutes} min</p>
        <h1>{unit.title}</h1>
        <p className="lede">{unit.body_md}</p>

        {unit.type === 'drill_deck' ? (
          <div className="card">
            <div className="card__title">
              <span>{cards} {cards === 1 ? 'Card' : 'Cards'}</span>
            </div>
            <p style={{ margin: 0 }}>
              A deck is scheduled, not browsed. The server decides which of its
              Cards are due and puts them in Today's queue — playing the whole deck
              on demand would replace that schedule with guesswork, so there is no
              button for it here.
            </p>
          </div>
        ) : unit.type === 'quiz' ? (
          <p className="hint">
            A Quiz opens in the quiz player. Reaching this view means the Unit could
            not be loaded for it — go back and try again once you are online.
          </p>
        ) : (
          <p className="hint">
            A Lab is submitted from the Mission step that carries it, so there is
            no standalone player. Its brief is above; open the Mission to do the
            work and submit it for review.
          </p>
        )}

        <SourcesCard unit={unit} open={showSources} onToggle={() => setShowSources((v) => !v)} />
      </main>
    );
  }

  const sections = unit.sections_md ?? [];

  return (
    <main className="screen">
      <button type="button" className="back" onClick={onBack}>← Back</button>

      <p className="eyebrow">Lesson · {unit.est_minutes} min</p>
      <h1>{unit.title}</h1>
      {completed ? <span className="badge badge--good">Completed</span> : null}
      <p className="lede">{unit.body_md}</p>

      {sections.map((section, i) => (
        <section
          key={section.id}
          className="lesson__section"
          ref={i === sections.length - 1 ? lastSectionRef : undefined}
        >
          <h2 className="lesson__heading">{section.heading}</h2>
          <Markdown source={section.md} />
        </section>
      ))}

      <SourcesCard unit={unit} open={showSources} onToggle={() => setShowSources((v) => !v)} />

      <div className="lesson__gate">
        <button type="button" onClick={complete} disabled={!reachedEnd || completed}>
          {completed ? 'Lesson completed' : 'Complete lesson'}
        </button>
        {!reachedEnd && !completed ? (
          <p className="hint">Scroll to the end of the lesson to enable this.</p>
        ) : null}
        {completed ? (
          <p className="hint">
            Saved. It reaches the server with the next outbox drain — and the
            Topic's Cards start appearing in Today once it lands.
          </p>
        ) : null}
      </div>
    </main>
  );
}
